/**
 * Schema for the Home Assistant flow graph — barrel export.
 *
 * Declares node types, their ports and properties, the ports of UI objects in
 * Home Assistant mode, and the palette shown in the left sidebar. Graph-state
 * rules (max wires, one trigger per object event, one binding per property)
 * live in the flow model; everything here is static.
 */
import { getBindableProperties } from '../runtime.js'
import { getAvailableEvents, getObjectSchema } from '../wireFormat.js'
import { filterPaletteForWire } from '../graphPorts.js'
import {
    BINDING_NODE_TYPES,
    conditionNode,
    entityNode,
    HASS_ACTION_NODE_TYPES,
    HASS_ACTION_VARIANTS,
    switchNode,
    triggerNode,
} from './flowNodes.js'
import { resolveNodePorts } from '../graphPorts.js'
import { HASS_OBJECT_EVENTS_PORT, HASS_OBJECT_IN_PORT, HASS_PORT_KIND, isPortCompatible } from './ports.js'

export { describeSwitchCase, SWITCH_ELSE_PORT, switchCasePort } from './flowNodes.js'
export {
    BINDING_NODE_TYPES, HASS_ACTION_NODE_TYPES, HASS_ACTION_VARIANTS,
    HASS_OBJECT_EVENTS_PORT, HASS_OBJECT_IN_PORT, HASS_PORT_KIND, isPortCompatible,
}

export const HASS_NODE_SCHEMAS = Object.freeze({
    entity: entityNode,
    ...BINDING_NODE_TYPES,
    switch: switchNode,
    trigger: triggerNode,
    condition: conditionNode,
    ...HASS_ACTION_NODE_TYPES,
})

export function getHassNodeSchema(type) {
    return HASS_NODE_SCHEMAS[type] ?? null
}

export function getHassInputPort(type, portId) {
    return getHassNodeSchema(type)?.ports.inputs.find(port => port.id === portId) ?? null
}

export function getHassOutputPort(type, portId) {
    return getHassNodeSchema(type)?.ports.outputs.find(port => port.id === portId) ?? null
}

/** A port of a node, including ports that follow its data (a switch's cases). */
export function getHassNodePort(node, dir, portId) {
    const ports = resolveNodePorts(getHassNodeSchema, node)
    return (dir === 'in' ? ports.inputs : ports.outputs).find(port => port.id === portId) ?? null
}

const schemaOf = source => (typeof source === 'string'
    ? getObjectSchema(source)
    : source?.schemaV2 ?? getObjectSchema(source?.obj) ?? null)

/**
 * Events a trigger can listen to on `source` (a node, schema or obj type):
 * the published events only. Gestures run on the device and never reach
 * Home Assistant.
 */
export function getHassTriggerEvents(source) {
    return getAvailableEvents(source)
}

/**
 * Properties of `source` a binding can drive, in firmware wire names. A
 * Designer node carries them as `hass_properties` (widgets such as gauge and
 * badge add dynamic ones); otherwise they come from the schema.
 */
export function getHassBindableProperties(source) {
    if (Array.isArray(source?.hass_properties)) return [...source.hass_properties]
    const schema = schemaOf(source)
    return schema ? getBindableProperties(schema) : []
}

/**
 * Ports of a UI object in Home Assistant mode: an input for its Primary
 * Entity and bindings when it has bindable properties or events, and an
 * events output when it emits events.
 */
export function getHassObjectPorts(source) {
    const hasEvents = getHassTriggerEvents(source).length > 0
    const bindable = getHassBindableProperties(source).length > 0
    return {
        inputs: hasEvents || bindable ? [HASS_OBJECT_IN_PORT] : [],
        outputs: hasEvents ? [HASS_OBJECT_EVENTS_PORT] : [],
    }
}

const entry = (schema, nodeType, extra = {}) => ({
    id: nodeType, nodeType, label: schema.label, icon: schema.icon, color: schema.color, preset: {}, ...extra,
})

/** Palette groups for the "Home Assistant" sidebar section, in display order. */
export function getHassPalette() {
    const bindings = ['state', 'mapping', 'constant', 'template']
    return [
        {
            id: 'entities',
            label: 'Entities',
            color: entityNode.color,
            entries: [entry(entityNode, 'entity', {
                title: 'Home Assistant entity: wire it to bindings, to an object (its Primary Entity) or to action targets',
            })],
        },
        {
            id: 'bindings',
            label: 'Bindings',
            color: BINDING_NODE_TYPES.state.color,
            entries: [
                ...bindings.map(type => entry(BINDING_NODE_TYPES[type], type, {
                    id: `binding:${type}`,
                    title: `${BINDING_NODE_TYPES[type].label}: drives a property of the object it is wired to`,
                })),
                entry(switchNode, 'switch', {
                    title: 'Switch: the bindings wired to the first case its entity matches apply, else those on Else',
                }),
            ],
        },
        {
            id: 'triggers',
            label: 'Triggers',
            color: triggerNode.color,
            entries: [
                entry(triggerNode, 'trigger', { title: 'Trigger: choose which event of its source object in the properties' }),
                entry(conditionNode, 'condition', { title: 'Condition: a branch of its trigger; the first branch that holds runs' }),
            ],
        },
        {
            id: 'actions',
            label: 'Actions',
            color: HASS_ACTION_NODE_TYPES.service.color,
            entries: HASS_ACTION_VARIANTS.map(variant => ({
                ...variant,
                color: HASS_ACTION_NODE_TYPES[variant.nodeType].color,
                title: `${variant.label}: a Home Assistant action run by a trigger or condition`,
            })),
        },
    ]
}

/** Palette groups narrowed to nodes that could take an open wire end (see filterPaletteForWire). */
export function getHassPaletteForWire(dir, port) {
    return filterPaletteForWire(getHassPalette(), getHassNodeSchema, dir, port)
}
